import React, { useState, useEffect, useRef } from 'react';
import api from '../../../../../api';
import './VoiceModals.css';

const CnamModal = ({ userId, cnamData, isEditMode, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const errorRef = useRef(null);
  const [formData, setFormData] = useState({
    endUserName: '',
    cname: '',
  });
  const [errors, setErrors] = useState({ endUserName: '', cname: '' });

  useEffect(() => {
    if (isEditMode && cnamData) {
      setFormData({
        endUserName: cnamData.end_user_friendly_name || '',
        cname: cnamData.friendly_name || '',
      });
    }
  }, [isEditMode, cnamData]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Scroll to error when it appears
  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [error]);

  const validate = () => {
    const e = { endUserName: '', cname: '' };
    let ok = true;

    if (!formData.endUserName.trim()) {
      e.endUserName = 'End User Friendly Name is required';
      ok = false;
    }

    if (!formData.cname.trim()) {
      e.cname = 'CNAM is required';
      ok = false;
    } else if (formData.cname.length > 15) {
      e.cname = 'CNAM cannot exceed 15 characters';
      ok = false;
    } else if (!/^[A-Za-z][A-Za-z0-9., ]*$/.test(formData.cname)) {
      e.cname = 'CNAM must start with a letter and can only contain letters, numbers, periods, commas, and spaces';
      ok = false;
    }

    setErrors(e);
    return ok;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validate()) return;

    setLoading(true);
    try {
      let response;
      if (isEditMode) {
        response = await api.outbound.updateCNAM({
          id: cnamData.id,
          end_user_friendly_name: formData.endUserName,
          friendly_name: formData.cname,
          user_id: parseInt(userId),
        });
      } else {
        response = await api.outbound.saveCNAM({
          end_user_friendly_name: formData.endUserName,
          friendly_name: formData.cname,
          user_id: parseInt(userId),
        });
      }

      if (response?.status) {
        onClose(true); // Close modal and trigger refresh
      } else {
        setError(response?.message || (isEditMode ? 'Failed to update CNAM' : 'Failed to create CNAM'));
      }
    } catch (err) {
      console.error('CNAM Submit Error:', err);
      setError(err?.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    setErrors(prev => ({
      ...prev,
      [name]: ''
    }));
  };

  return (
    <div className="voice-modal-overlay">
      <div className="voice-modal-content">
        <div className="voice-modal-header">
          <h3>{isEditMode ? 'Edit CNAM' : '➕ Create CNAM'}</h3>
          <button
            className="voice-modal-close"
            onClick={() => onClose(false)}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="voice-modal-body">
            {error && (
              <div className="alert alert-danger" ref={errorRef}>
                {error}
              </div>
            )}

            <div className="info-box">
              <p>
                CNAM (Caller ID Name) allows you to display your business name on recipient's
                caller ID, increasing answer rates and building trust.
              </p>
            </div>

            <div className="form-group">
              <label>End User Friendly Name <span className="required">*</span></label>
              <input
                type="text"
                name="endUserName"
                className={`form-input ${errors.endUserName ? 'error' : ''}`}
                value={formData.endUserName}
                onChange={handleInputChange}
                placeholder="e.g., Your Company Name"
                disabled={loading}
              />
              {errors.endUserName && (
                <span className="error-message">{errors.endUserName}</span>
              )}
              <span className="field-hint">Name to identify the end user or business</span>
            </div>

            <div className="form-group">
              <label>Caller ID Name (CNAM) <span className="required">*</span></label>
              <input
                type="text"
                name="cname"
                className={`form-input ${errors.cname ? 'error' : ''}`}
                value={formData.cname}
                onChange={handleInputChange}
                placeholder="e.g., YourCompany"
                maxLength="15"
                disabled={loading}
              />
              {errors.cname && (
                <span className="error-message">{errors.cname}</span>
              )}
              <span className="field-hint">
                Max 15 characters. Must start with a letter. Can contain letters, numbers, periods, commas, and spaces.
              </span>
              <div className="character-count">
                {formData.cname.length}/15 characters
              </div>
            </div>
          </div>

          <div className="voice-modal-footer">
            <button
              type="button"
              className="btn linear-gradient-cancel yr-cancelbtn mg-r"
              onClick={() => onClose(false)}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn linear-gradient zy-save-btn ml-2"
              disabled={loading}
            >
              {loading && (
                <span className="spinner-border spinner-border-sm" style={{ marginRight: '8px' }} role="status" aria-hidden="true"></span>
              )}
              {loading ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update CNAM' : 'Create CNAM')}
            </button>
          </div>
        </form>

        {/* Loading Overlay */}
        {loading && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            borderRadius: '8px'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div className="ui active inline loader" style={{ marginBottom: '15px' }}></div>
              <p style={{ color: '#087ab6', fontSize: '14px', fontWeight: 500 }}>
                {isEditMode ? 'Updating CNAM...' : 'Creating CNAM...'}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CnamModal;
